import type { ClosetSpec, Part } from "./types";

/**
 * Compute the ceiling height at a given X position (0..totalWidth).
 * Used to derive the trimmed top edge of side / divider panels under a sloped ceiling.
 */
export function ceilingHeightAt(spec: ClosetSpec, x: number): number {
  const { ceiling, totalWidth } = spec;
  if (ceiling.type === "flat") return ceiling.heightLeft;
  if (ceiling.type === "slanted") {
    const t = totalWidth === 0 ? 0 : x / totalWidth;
    return ceiling.heightLeft + (ceiling.heightRight - ceiling.heightLeft) * t;
  }
  // gable: eaves on both sides at heightLeft, peak at center at heightRight
  const half = totalWidth / 2;
  const dist = Math.abs(x - half);
  const t = half === 0 ? 1 : dist / half; // 0 at peak, 1 at eaves
  return ceiling.heightRight + (ceiling.heightLeft - ceiling.heightRight) * t;
}

/** Compute the X position of every vertical panel (outer sides + dividers). */
export function panelXPositions(spec: ClosetSpec): number[] {
  const t = spec.material.carcassThickness;
  const xs: number[] = [0];
  let x = t; // inside face of left wall
  for (let i = 0; i < spec.sections.length; i++) {
    x += spec.sections[i].width;
    xs.push(x);
    if (i < spec.sections.length - 1) x += t;
  }
  // ensure exterior right edge:
  if (xs[xs.length - 1] !== spec.totalWidth) {
    xs[xs.length - 1] = spec.totalWidth - t;
    xs.push(spec.totalWidth);
  } else {
    xs.push(spec.totalWidth);
  }
  return xs;
}

/**
 * Generate the flat list of Parts needed to build the closet.
 * Conservative MVP: outer sides, internal dividers, top, bottom, back, shelves, toe kick.
 * Drawers/doors are tracked as notes only (out of MVP scope to fully cut).
 */
export function generateParts(spec: ClosetSpec): Part[] {
  const parts: Part[] = [];
  const m = spec.material;
  const t = m.carcassThickness;

  // ---- Vertical panels (outer sides + dividers) ----
  // For sloped ceilings, every vertical panel becomes a trapezoid.
  // We use the X-center of each panel to pick its top height (so a 3/4" panel
  // gets one representative height — easy for the operator to mark).
  const verticalCount = spec.sections.length + 1;
  for (let i = 0; i < verticalCount; i++) {
    // X-center of panel i
    let xc: number;
    if (i === 0) xc = t / 2;
    else if (i === verticalCount - 1) xc = spec.totalWidth - t / 2;
    else {
      // sum widths of sections to the left of this divider
      let x = t;
      for (let k = 0; k < i; k++)
        x += spec.sections[k].width + (k < i - 1 ? t : 0);
      xc = x + t / 2;
    }
    const topH = ceilingHeightAt(spec, xc);
    const isOuter = i === 0 || i === verticalCount - 1;
    const label = isOuter
      ? i === 0
        ? "Left Side (gable)"
        : "Right Side (gable)"
      : `Divider ${i}`;

    if (spec.ceiling.type === "flat") {
      parts.push({
        id: `panel-${i}`,
        label,
        width: spec.depth,
        length: topH,
        thickness: t,
      });
    } else {
      // Trapezoid: take a rect at the TALLER edge and trim down to the shorter.
      const leftH = i === 0 ? topH : ceilingHeightAt(spec, xc - t / 2);
      const rightH = ceilingHeightAt(spec, xc + t / 2);
      const longEdge = Math.max(leftH, rightH);
      const shortEdge = Math.min(leftH, rightH);
      parts.push({
        id: `panel-${i}`,
        label,
        width: spec.depth,
        length: longEdge,
        thickness: t,
        angledTop: true,
        shortEdge,
        notes: `Trapezoid: long edge ${longEdge.toFixed(1)} cm, short edge ${shortEdge.toFixed(1)} cm`,
      });
    }
  }

  // ---- Top & bottom horizontal stretchers (one per section) ----
  // Under a sloped ceiling we use a low horizontal "header" at min ceiling height of the section
  // so doors/drawers have a reference; otherwise a full-width top runs across.
  for (let i = 0; i < spec.sections.length; i++) {
    const sec = spec.sections[i];
    parts.push({
      id: `bottom-${sec.id}`,
      label: `Bottom — ${sec.id}`,
      width: spec.depth,
      length: sec.width,
      thickness: t,
    });
    if (spec.ceiling.type === "flat") {
      parts.push({
        id: `top-${sec.id}`,
        label: `Top — ${sec.id}`,
        width: spec.depth,
        length: sec.width,
        thickness: t,
      });
    } else {
      // 10 cm wide stretcher front + back instead of full top
      parts.push({
        id: `stretcher-front-${sec.id}`,
        label: `Top stretcher (front) — ${sec.id}`,
        width: 10,
        length: sec.width,
        thickness: t,
      });
      parts.push({
        id: `stretcher-back-${sec.id}`,
        label: `Top stretcher (back) — ${sec.id}`,
        width: 10,
        length: sec.width,
        thickness: t,
      });
    }
  }

  // ---- Shelves ----
  for (const sec of spec.sections) {
    for (let s = 0; s < sec.shelfCount; s++) {
      parts.push({
        id: `shelf-${sec.id}-${s}`,
        label: `Shelf ${s + 1} — ${sec.id}`,
        width: spec.depth - 2.5, // 25 mm set back from front
        length: sec.width - 0.2, // tiny clearance
        thickness: t,
      });
    }
    if (sec.drawerStack) {
      // 4 drawer fronts (MVP, doesn't fully spec the drawer box)
      for (let d = 0; d < 4; d++) {
        parts.push({
          id: `drawer-front-${sec.id}-${d}`,
          label: `Drawer front ${d + 1} — ${sec.id}`,
          width: 15,
          length: sec.width - 0.6,
          thickness: t,
          notes: "Edge-band all 4 sides.",
        });
      }
    }
  }

  // ---- Toe kick (single front rail) ----
  if (spec.toeKickHeight > 0) {
    parts.push({
      id: "toekick",
      label: "Toe kick (front)",
      width: spec.toeKickHeight,
      length: spec.totalWidth,
      thickness: t,
    });
  }

  // ---- Back panel (1/4" stock) ----
  // Single piece if it fits a sheet; otherwise split into per-section panels.
  const backHeight = Math.max(
    ceilingHeightAt(spec, 0),
    ceilingHeightAt(spec, spec.totalWidth / 2),
    ceilingHeightAt(spec, spec.totalWidth),
  );
  const fitsOne =
    Math.max(spec.totalWidth, backHeight) <= m.sheetLength &&
    Math.min(spec.totalWidth, backHeight) <= m.sheetWidth;
  if (fitsOne) {
    parts.push({
      id: "back",
      label: "Back panel",
      width: backHeight,
      length: spec.totalWidth,
      thickness: m.backThickness,
      notes:
        spec.ceiling.type === "flat"
          ? undefined
          : "Trim top edge to ceiling profile on site.",
    });
  } else {
    for (const sec of spec.sections) {
      parts.push({
        id: `back-${sec.id}`,
        label: `Back panel — ${sec.id}`,
        width: backHeight,
        length: sec.width + t, // overlap onto dividers
        thickness: m.backThickness,
      });
    }
  }

  return parts;
}
